import { useCallback, useEffect, useState } from 'react'
import Cropper, { Area, MediaSize } from 'react-easy-crop'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import { BaseRecord } from '../../models/BaseRecord'
import { Reader } from '../../models/Reader'
import { RecordFactory } from '../../models/RecordFactory'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { getImageDimensionsFromUrl, resizeImageFile } from '../../utils/files'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import { Spinner } from '../Spinner'
import Button from '../app/design/Button'
import Modal from '../app/design/Modal'
import { WarningModal } from '../common/modals/WarningModal'
import { Select } from '../common/fields/Select'
import { TextField } from '../common/fields/TextField'
import { readerTypeOptions } from '../settings/Options'
import { WorkflowPage } from '../workflows/WorkflowPage'

const STRIPE_SPLASHSCREEN_MAX_DIMS = { width: 720, height: 1280 }

const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image()
        image.addEventListener('load', () => resolve(image))
        image.addEventListener('error', (error) => reject(error))
        image.src = url
    })

const getCroppedImg = async (imageSrc: string, pixelCrop: Area): Promise<File | undefined> => {
    const image = await createImage(imageSrc)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) {
        return
    }

    // set canvas size to match the bounding box
    canvas.width = image.width
    canvas.height = image.height

    // draw rotated image
    ctx.drawImage(image, 0, 0)

    // croppedAreaPixels values are bounding box relative
    // extract the cropped image using these values
    const data = ctx.getImageData(pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height)

    // set canvas width to final desired crop size - this will clear existing context
    canvas.width = pixelCrop.width
    canvas.height = pixelCrop.height
    ctx.putImageData(data, 0, 0)

    // As a blob
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob: Blob | null) => {
            if (!blob || blob == null) {
                reject()
            }
            else {
                resolve(
                    new File([blob], 'croppedImage.jpg', {
                        type: 'image/jpeg',
                        lastModified: Date.now(),
                    })
                )
            }
        }, 'image/jpeg')
    })
}

const viewWillAppear = async (base: PocketComponent) => {
    let reader = base.locationState?.reader
        ? BaseRecord.fromSerializer(base.locationState?.reader, Reader)
        : undefined
    if (reader?.id) {
        reader = await RecordFactory.getInstance().loadReader(reader.id)
    }
    if (!reader) {
        reader = new Reader()
        reader.type = 'bbpos_wisee'
        reader.line1 = base.me.line1 === '' ? undefined : base.me.line1
        reader.line2 = base.me.line2 === '' ? undefined : base.me.line2
        reader.city = base.me.city === '' ? undefined : base.me.city
        reader.state = base.me.state
        reader.postalCode = base.me.postalCode
    }

    return { reader, image: reader.image }
}

const render = ({ base, data }: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    if (data.reader === undefined) {
        throw new RecordNotFoundError()
    }

    const params = base.urlParams
    const navigate = usePSNavigate()
    const [reader, setReader] = useState(data.reader)
    const [isLoading, setIsLoading] = useState(false)
    const [showLearnMoreModal, setShowLearnMoreModal] = useState(false)
    const [showImageTooLargeModal, setShowImageTooLargeModal] = useState(false)

    const isActivate = !!params.readerID && location.pathname.endsWith('activate')
    const isEdit = !!params.readerID && location.pathname.endsWith('edit')

    // Image handling states
    const [croppedFile, setCroppedFile] = useState<File>()
    const [photo, setPhoto] = useState<
        { type: 'file'; file: File } | { type: 'url'; url: string } | null
    >(data.image?.url ? { type: 'url', url: data.image.url.toString() } : null)
    const [srcFile, setSrcFile] = useState<string | ArrayBuffer | null>()
    const [imageSize, setImageSize] = useState<{ width: number; height: number } | undefined>(
        undefined
    )

    // Cropper states
    const [crop, setCrop] = useState({ x: 0, y: 0 })
    const [zoom, setZoom] = useState(1)
    const requiredAspectRatio = 720 / 1280
    const containerWidth = 568
    const containerHeight = 426

    if (isActivate && !reader?.id) {
        navigate(-1)
    }

    const handlePhotoSelected = (file: File) => {
        setPhoto({ type: 'file', file })
        const fileReader = new FileReader()
        fileReader.addEventListener('load', () => {
            setSrcFile(fileReader.result)
        })
        fileReader.readAsDataURL(file)
    }

    const onCropComplete = useCallback(
        (croppedArea: Area, croppedAreaPixels: Area) => {
            if (!srcFile || typeof srcFile !== 'string') return

            getCroppedImg(srcFile, croppedAreaPixels).then((file?: File) => {
                if (file) {
                    setCroppedFile(file)
                    setImageSize({
                        width: croppedAreaPixels.width,
                        height: croppedAreaPixels.height,
                    })
                }
            })
        },
        [srcFile]
    )

    useEffect(() => {
        if (data.image?.url) {
            getImageDimensionsFromUrl(data.image.url)
                .then(({ width, height }) => {
                    setImageSize({ width: width, height: height })
                })
                .catch((error) => {
                    console.error('Error getting uploaded image dimensions:', error)
                })
        }
    }, [data.image?.url])

    const onMediaLoaded = useCallback(
        (mediaSize: MediaSize) => {
            const height = mediaSize.naturalHeight
            const width = Math.min(
                mediaSize.naturalWidth,
                mediaSize.naturalHeight * requiredAspectRatio
            )
            const minPixelBlur = (720 * 1280) / 4 // pixelation in 2 pixels in each direction
            if (height * width < minPixelBlur) {
                console.log('warning low resolution image')
            }
        },
        [requiredAspectRatio]
    )

    const saveReader = async () => {
        if (!base.owner) return

        let newCroppedFile = croppedFile
        if (
            imageSize &&
            (imageSize.width > STRIPE_SPLASHSCREEN_MAX_DIMS.width ||
                imageSize.height > STRIPE_SPLASHSCREEN_MAX_DIMS.height)
        ) {
            if (croppedFile) {
                try {
                    setIsLoading(true)
                    newCroppedFile = await resizeImageFile(
                        croppedFile,
                        STRIPE_SPLASHSCREEN_MAX_DIMS.width,
                        STRIPE_SPLASHSCREEN_MAX_DIMS.height
                    )
                }
                catch (error) {
                    console.error('Error resizing image:', error)
                    setShowImageTooLargeModal(true)
                    setIsLoading(false)
                    return
                }
            }
            else {
                setShowImageTooLargeModal(true) // in case too-large image was uploaded in the past
                return
            }
        }

        setIsLoading(true)
        try {
            const result = await RecordFactory.getInstance().sendReader(reader, newCroppedFile)
            if (!result.ok) {
                base.logAndDisplayError(result.error)
                return
            }
            navigate('/settings/reader')
        }
        finally {
            setIsLoading(false)
        }
    }

    const isLabelValid = !reader.isInternetReader || !!reader.label
    const saveEnabled =
        !isLoading &&
        isLabelValid &&
        (isEdit || !!reader.registrationCode) &&
        (reader.label !== data.reader.label ||
            !!srcFile ||
            (isActivate &&
                (reader.isInternetReader
                    ? reader.registrationCode !== data.reader.registrationCode
                    : reader.serialNumber !== data.reader.serialNumber)))

    return (
        <WorkflowPage
            title={isActivate ? 'Activate reader' : isEdit ? 'Edit reader' : 'Add existing reader'}
            footerContent={
                <div className="flex w-full items-center justify-end gap-x-6">
                    {isLoading && <Spinner size={32} />}
                    <Button intent="primary" disabled={!saveEnabled} onClick={saveReader}>
                        Save
                    </Button>
                </div>
            }
        >
            <div className="mt-12 flex w-full flex-col items-center justify-start">
                <WorkflowPage.Card>
                    {!isActivate && !isEdit && (
                        <WorkflowPage.IconSection
                            title="Choose a card reader"
                            icon="cardReader.large"
                        >
                            <Select
                                size="medium"
                                className="border-gray-500"
                                options={readerTypeOptions().map((option) => ({
                                    id: option.value,
                                    title: option.label,
                                }))}
                                onSelectOption={(selection) => {
                                    setReader(reader.clone({ type: selection.id }))
                                }}
                                selectedOptionID={reader.type}
                            />
                        </WorkflowPage.IconSection>
                    )}

                    {!isEdit && (
                        <WorkflowPage.IconSection
                            title={
                                reader.isInternetReader
                                    ? 'Registration code (required)'
                                    : 'Serial number (required)'
                            }
                            icon="hash.large"
                            thirdLine={
                                <a
                                    className="link text-primary-700 hover:text-primary-700/60 text-sm underline"
                                    onClick={() => setShowLearnMoreModal(true)}
                                >
                                    Learn more
                                </a>
                            }
                        >
                            <TextField
                                size="medium"
                                required
                                placeholder={
                                    reader.isInternetReader
                                        ? 'Enter registration code'
                                        : 'Enter serial number'
                                }
                                value={
                                    reader.isInternetReader
                                        ? reader.registrationCode
                                        : reader.serialNumber
                                }
                                onChange={(e) => {
                                    if (reader.isInternetReader) {
                                        setReader(
                                            reader.clone({ registration_code: e.target.value })
                                        )
                                    }
                                    else {
                                        setReader(reader.clone({ serial_number: e.target.value }))
                                    }
                                }}
                                onClear={() => {
                                    if (reader.isInternetReader) {
                                        setReader(reader.clone({ registration_code: '' }))
                                    }
                                    else {
                                        setReader(reader.clone({ serial_number: '' }))
                                    }
                                }}
                            />
                        </WorkflowPage.IconSection>
                    )}

                    <WorkflowPage.IconSection
                        title="Reader Label"
                        icon="field2.large"
                        description="What would you like to name this reader?"
                    >
                        <TextField
                            size="medium"
                            placeholder="Enter a label for reader"
                            value={reader.label}
                            onChange={(e) => {
                                setReader(reader.clone({ label: e.target.value }))
                            }}
                            onClear={() => setReader(reader.clone({ label: '' }))}
                        />
                    </WorkflowPage.IconSection>

                    {reader.isInternetReader && (
                        <WorkflowPage.IconSection
                            title="Splashscreen"
                            icon="image.large"
                            description={
                                isActivate && !photo
                                    ? `Add a splashscreen to your reader? (maximum dimensions: ${STRIPE_SPLASHSCREEN_MAX_DIMS.width} x ${STRIPE_SPLASHSCREEN_MAX_DIMS.height})`
                                    : ''
                            }
                        >
                            <div className="flex flex-col items-center">
                                {!srcFile ||
                                typeof srcFile !== 'string' ||
                                !photo ||
                                photo?.type === 'url' ? (
                                    <WorkflowPage.PhotoPicker
                                        onSelectPhoto={handlePhotoSelected}
                                        selectedPhoto={photo}
                                        shape="card-reader-rectangle"
                                        size="medium"
                                        onClearPhoto={() => setPhoto(null)}
                                    />
                                ) : (
                                    <>
                                        <Cropper
                                            style={{
                                                containerStyle: {
                                                    position: 'relative',
                                                    width: `${containerWidth}px`,
                                                    height: `${containerHeight}px`,
                                                },
                                            }}
                                            image={srcFile}
                                            crop={crop}
                                            zoom={zoom}
                                            aspect={requiredAspectRatio}
                                            onCropChange={setCrop}
                                            onCropComplete={onCropComplete}
                                            onMediaLoaded={onMediaLoaded}
                                            onZoomChange={setZoom}
                                        />
                                        <div
                                            className="controls"
                                            style={{ marginTop: '18px', display: 'flex' }}
                                        >
                                            <div style={{ fontSize: '20px' }}>-</div>
                                            <input
                                                type="range"
                                                value={zoom}
                                                min={1}
                                                max={3}
                                                step={0.1}
                                                className="zoom-range"
                                                style={{
                                                    height: '2px',
                                                    width: '100%',
                                                    margin: '14px',
                                                }}
                                                onChange={(e) => {
                                                    setZoom(Number(e.target.value))
                                                }}
                                            />
                                            <div style={{ fontSize: '20px' }}>+</div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </WorkflowPage.IconSection>
                    )}
                </WorkflowPage.Card>
            </div>

            <Modal
                isOpen={showLearnMoreModal}
                title="Registration code"
                onClose={() => setShowLearnMoreModal(false)}
            >
                <div className="text-gray-600">
                    To access your registration code swipe left to right on the screen and select
                    Settings. Enter the pin '07139' then select "Generate pairing code".
                </div>
            </Modal>

            {showImageTooLargeModal && (
                <WarningModal
                    title="Image too large"
                    message={`Splashscreen image must not exceed ${STRIPE_SPLASHSCREEN_MAX_DIMS.width}px x ${STRIPE_SPLASHSCREEN_MAX_DIMS.height}px. The image you selected is ${imageSize?.width}px x ${imageSize?.height}px.`}
                    buttonText="Ok"
                    onClose={() => setShowImageTooLargeModal(false)}
                    onButtonClick={() => setShowImageTooLargeModal(false)}
                />
            )}
        </WorkflowPage>
    )
}

export const ReaderPage = usePSComponent(render, viewWillAppear)
